import { Box, Text, Flex } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { getEthereumAddress } from '@ninjablaze/sdk-ts'
import ArrowUpRight from '@/public/assets/icons/arrow-up-right.svg'
import Link from 'next/link'
import { getNftColor } from '@/helpers/getNftColor'
import { getNftRole } from '@/helpers/getNftRole'
import { useQuery } from '@apollo/client'
import { GET_USER_PROFILE } from '@/api/query'
import { UserProfile } from '../templates/UserActivity/types'
import { EXPLORERS } from '@/constants/constants'

export const UserProfileSummary = ({
  user,
  nfts,
}: {
  user: UserProfile | null
  nfts: {
    type: string
    amount: number
  }[]
}) => {
  const { t } = useTranslation()
  const { data } = useQuery(GET_USER_PROFILE, {
    skip: !user?.referrer,
    variables: {
      address: user?.referrer!,
    },
  })

  const refferer = data?.user ? data?.user : null
  const numberOfNfts = nfts.reduce((prev, curr) => prev + curr.amount, 0)
  const isNftOwner = Boolean(numberOfNfts)

  return (
    <Box>
      <Flex
        h={refferer ? 'auto' : { base: 'auto', md: 'full' }}
        flexDir={'column'}
        justifyContent={'space-between'}
        p={6}
        rounded={'2xl'}
        bg={'ninja.600'}
      >
        <Box>
          <Flex gap={4} alignItems={'center'} mb={5}>
            <AvatarCircle offset={8} w={'71px'} h={'71px'} src={user?.profileAvatar || ''} />
            <Flex flexDir={'column'} alignItems={'start'}>
              {isNftOwner ? (
                <GradientText
                  numberOfNfts={numberOfNfts!}
                  props={{
                    fontWeight: 700,
                    fontSize: '2xl',
                  }}
                >
                  {user?.profileUserName}
                </GradientText>
              ) : (
                <Text color={'ninja.100'} fontSize={'2xl'} fontWeight={700}>
                  {user?.profileUserName}
                </Text>
              )}
              {isNftOwner && (
                <Box
                  rounded={'md'}
                  wordBreak={'keep-all'}
                  whiteSpace={'nowrap'}
                  fontSize={'xs'}
                  fontWeight={700}
                  px={1}
                  bg={getNftColor(numberOfNfts!)}
                >
                  {getNftRole(numberOfNfts!)}
                </Box>
              )}
            </Flex>
          </Flex>
          <StatsRow
            styles={{ bg: 'ninja.500' }}
            value={<CopiableLink maxLength={16} value={getEthereumAddress(user?.address!)} />}
            name={t('common.evm-wallet')}
          />
          <StatsRow
            styles={{ bg: 'ninja.500' }}
            value={
              <CopiableLink
                maxLength={16}
                value={user?.address!}
                link={`${EXPLORERS.INJECTIVE_WALLETS}${user?.address!}`}
              />
            }
            name={t('common.inj-wallet')}
          />
        </Box>
        <StatsRow
          styles={{ bg: 'ninja.500', mb: 0 }}
          value={new Date(user?.created!).toLocaleString()}
          name={t('common.joined')}
        />
      </Flex>
      {refferer && (
        <Box
          mt={4}
          color={'ninja.200'}
          _hover={{
            color: 'ninja.100',
          }}
        >
          <Link href={'/activity/' + user?.referrer}>
            <Flex p={4} bg={'ninja.600'} rounded={'xl'} alignItems={'center'} justifyContent={'space-between'}>
              <Flex gap={3}>
                <AvatarCircle offset={6} boxSize={10} src={refferer?.profileAvatar || ''} />
                <Flex flexDir={'column'} justifyContent={'center'}>
                  <Text lineHeight={1} color={'ninja.100'} fontSize={'sm'} fontWeight={500} mb={0.5}>
                    {t('common.invited-by')}
                  </Text>
                  <Text color={'white'} lineHeight={1} fontSize={'xl'} fontWeight={700}>
                    {refferer?.profileUserName}
                  </Text>
                </Flex>
              </Flex>
              <Box w={6}>
                <ArrowUpRight />
              </Box>
            </Flex>
          </Link>
        </Box>
      )}
    </Box>
  )
}
